import { Box, Stack } from '@mui/system';
import { FC } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { JourneyHeader } from './JourneyHeader';
import { z } from 'zod';
import { useTypedPathParams } from '../../../shared/fe/hooks/useTypedPathParams';
import { useApiQuery } from '../query/hooks';
import { CircularProgress } from '@mui/material';
import { graphql } from '../gql';
import { detailSubactivtySchema } from './detail/JourneyDetailPage';
import { useDrawerContext } from '../drawer/DrawerContext';
import { JourneyHeaderActions } from './JourneyHeaderActions';
import { DetailContentScrollbar } from './DetailContentScrollbar';
import { HSMCProvider } from './HorizontalScrollMapContext';

const journeyWarnings_getJourney = graphql(`
	query journeyWarnings_getJourney($id: UUID!) {
		getJourney(by: { id: $id }) {
			id
			name
			color
			satisfaction
			widgets {
				id
				order
				name
				type
			}
			phases {
				id
			}
			personas {
				id
				...PersonaAvatar_persona
			}
			activityWarnings {
				id
			}
			mainMetric {
				metric {
					id
					unitSign
					dataSet {
						metricTemplate {
							unitSign
						}
					}
				}
			}
		}
	}
`);

const journey_getActivity = graphql(`
	query GetActivity($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			touchpoint {
				parentActivity {
					name
				}
			}
		}
	}
`);

export const detailLayoutSchema = z.object({
	id: z.string(),
});

export const JourneyDetailLayout: FC = () => {
	const { id } = useTypedPathParams(detailLayoutSchema);
	const { subactivityId } = useTypedPathParams(detailSubactivtySchema);
	const { setDrawer, setDrawerWithoutPrevParams } = useDrawerContext();
	const { data: journey, isLoading } = useApiQuery(journeyWarnings_getJourney, { variables: { id } });
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { data } = useApiQuery(journey_getActivity, { variables: { activityId: subactivityId ?? '' }, enabled: subactivityId != null });
	const isSubactivity = /^\/journeys\/[^/]+\/detail\/subactivity\/[^/]+$/.test(pathname);

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<CircularProgress />
			</Box>
		);
	}

	const metricUnitSign =
		journey?.getJourney?.mainMetric?.metric?.unitSign ?? journey?.getJourney?.mainMetric?.metric?.dataSet?.metricTemplate?.unitSign ?? null;

	return (
		journey?.getJourney != null && (
			<Stack
				spacing={2}
				flex={1}
				height={'100%'}
				sx={{
					overflow: 'hidden',
					'@media screen and (max-height: 420px) and (orientation: landscape)': {
						minHeight: 'calc(100svh + 64px)',
						overflow: 'auto',
					},
				}}
			>
				<JourneyHeader
					journeyId={id}
					journeyColor={journey.getJourney.color}
					mainMetricId={journey.getJourney.mainMetric?.metric?.id ?? null}
					metricUnitSign={metricUnitSign}
					title={isSubactivity ? data?.getActivity?.touchpoint?.parentActivity?.name : journey.getJourney.name}
					headerRedirect={isSubactivity ? () => navigate(`/journeys/${id}/detail`) : undefined}
					onEditDrawer={
						isSubactivity
							? () => setDrawer('editName', { activityId: subactivityId ?? '' })
							: () => setDrawerWithoutPrevParams('editJourney', { journeyId: id })
					}
					headerActionsComponent={isSubactivity ? null : <JourneyHeaderActions journey={journey} />}
				/>
				<HSMCProvider>
					<DetailContentScrollbar>
						<Outlet />
					</DetailContentScrollbar>
				</HSMCProvider>
			</Stack>
		)
	);
};
